import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["case-setup"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      caseSetupChecklist: build.query<
        CaseSetupChecklistApiResponse,
        CaseSetupChecklistApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/`,
        }),
        providesTags: ["case-setup"],
      }),
      caseSetupDraftIntroEmail: build.mutation<
        CaseSetupDraftIntroEmailApiResponse,
        CaseSetupDraftIntroEmailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/intro-email/`,
          method: "POST",
          body: queryArg.draftCaseSetupMessageRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
      caseSetupDraftIntroSms: build.mutation<
        CaseSetupDraftIntroSmsApiResponse,
        CaseSetupDraftIntroSmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/intro-sms/`,
          method: "POST",
          body: queryArg.draftCaseSetupMessageRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
      caseSetupDraftNudgeEmail: build.mutation<
        CaseSetupDraftNudgeEmailApiResponse,
        CaseSetupDraftNudgeEmailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/nudge-email/`,
          method: "POST",
          body: queryArg.draftCaseSetupMessageRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
      caseSetupDraftNudgeSms: build.mutation<
        CaseSetupDraftNudgeSmsApiResponse,
        CaseSetupDraftNudgeSmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/nudge-sms/`,
          method: "POST",
          body: queryArg.draftCaseSetupMessageRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
      caseSetupSendIntroEmail: build.mutation<
        CaseSetupSendIntroEmailApiResponse,
        CaseSetupSendIntroEmailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/send-intro-email/`,
          method: "POST",
          body: queryArg.sendCaseSetupEmailRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
      caseSetupSendIntroSms: build.mutation<
        CaseSetupSendIntroSmsApiResponse,
        CaseSetupSendIntroSmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/case-setup/messages/send-intro-sms/`,
          method: "POST",
          body: queryArg.sendCaseSetupSmsRequest,
        }),
        invalidatesTags: ["case-setup"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type CaseSetupChecklistApiResponse =
  /** status 200  */ CaseSetupChecklistRead[];
export type CaseSetupChecklistApiArg = {
  matterId: string;
};
export type CaseSetupDraftIntroEmailApiResponse =
  /** status 200  */ DraftCaseSetupEmailResult;
export type CaseSetupDraftIntroEmailApiArg = {
  matterId: string;
  draftCaseSetupMessageRequest: DraftCaseSetupMessageRequest;
};
export type CaseSetupDraftIntroSmsApiResponse =
  /** status 200  */ DraftCaseSetupSmsResult;
export type CaseSetupDraftIntroSmsApiArg = {
  matterId: string;
  draftCaseSetupMessageRequest: DraftCaseSetupMessageRequest;
};
export type CaseSetupDraftNudgeEmailApiResponse =
  /** status 200  */ DraftCaseSetupEmailResult;
export type CaseSetupDraftNudgeEmailApiArg = {
  matterId: string;
  draftCaseSetupMessageRequest: DraftCaseSetupMessageRequest;
};
export type CaseSetupDraftNudgeSmsApiResponse =
  /** status 200  */ DraftCaseSetupSmsResult;
export type CaseSetupDraftNudgeSmsApiArg = {
  matterId: string;
  draftCaseSetupMessageRequest: DraftCaseSetupMessageRequest;
};
export type CaseSetupSendIntroEmailApiResponse =
  /** status 200  */ SendCaseSetupEmailResult;
export type CaseSetupSendIntroEmailApiArg = {
  matterId: string;
  sendCaseSetupEmailRequest: SendCaseSetupEmailRequest;
};
export type CaseSetupSendIntroSmsApiResponse =
  /** status 200  */ SendCaseSetupSmsResult;
export type CaseSetupSendIntroSmsApiArg = {
  matterId: string;
  sendCaseSetupSmsRequest: SendCaseSetupSmsRequest;
};
export type CaseSetupItemKey =
  | "FIRST_PARTY_COVERAGE"
  | "FIRST_PARTY_HEALTH_COVERAGE"
  | "THIRD_PARTY_COVERAGE"
  | "FIRST_PARTY_AUTO_POLICY_DEC_PAGE"
  | "PHOTO_ID"
  | "SIGNED_HIPAA";
export type CaseSetupItemStatus = "COMPLETE" | "MISSING";
export type CaseSetupChecklistItem = {
  key: CaseSetupItemKey;
  label: string;
  status: CaseSetupItemStatus;
  detail?: string | null;
};
export type CaseSetupChecklist = {
  items: CaseSetupChecklistItem[];
};
export type CaseSetupChecklistRead = {
  items: CaseSetupChecklistItem[];
  missing_items: CaseSetupChecklistItem[];
  complete_items: CaseSetupChecklistItem[];
  is_complete: boolean;
  completion_count: number;
  total_count: number;
};
export type MatterType =
  | "Motor Vehicle Accident"
  | "Medical Malpractice"
  | "Miscellaneous"
  | "Breach of Employment Contract"
  | "Seller Non-Disclosure"
  | "Premises Liability"
  | "Broker Negligence"
  | "Property Damage"
  | "Dog Bite"
  | "Wrongful Death"
  | "Unknown";
export type DraftCaseSetupEmailResult = {
  subject: string;
  body_html: string;
  body_text: string;
  matter_type?: MatterType;
};
export type DraftCaseSetupMessageRequest = {
  matter_id: string | string;
  recipient_first_name_override?: string | null;
  language_override?: string | null;
};
export type DraftCaseSetupSmsResult = {
  message: string;
  character_count: number;
  matter_type?: MatterType;
};
export type SendCaseSetupEmailResult = {
  sent: boolean;
  conversation_id?: string | null;
};
export type SendCaseSetupEmailRequest = {
  matter_id: string | string;
  subject: string;
  body: string;
};
export type SendCaseSetupSmsResult = {
  sent: boolean;
  message: string;
};
export type SendCaseSetupSmsRequest = {
  matter_id: string | string;
  message: string;
};
export const {
  useCaseSetupChecklistQuery,
  useCaseSetupDraftIntroEmailMutation,
  useCaseSetupDraftIntroSmsMutation,
  useCaseSetupDraftNudgeEmailMutation,
  useCaseSetupDraftNudgeSmsMutation,
  useCaseSetupSendIntroEmailMutation,
  useCaseSetupSendIntroSmsMutation,
} = injectedRtkApi;
